import { provinciasRD, rolVehiculoOptions } from "@/lib/affiliateOptions";
import { Field, SectionCard, StepShell, inputClass } from "./ui";
import type { AfiliacionForm } from "./types";

interface StepDatosProps {
  form: AfiliacionForm;
  setField: <K extends keyof AfiliacionForm>(name: K, value: AfiliacionForm[K]) => void;
  errors: Record<string, string>;
}

export default function StepDatos({ form, setField, errors }: StepDatosProps) {
  const isEmpresa = form.tipo === "empresa";

  return (
    <StepShell
      title={isEmpresa ? "Información de la empresa" : "Datos personales"}
      description={
        isEmpresa
          ? "Cuéntanos sobre tu empresa de transporte y su persona de contacto."
          : "Completa tus datos de contacto y cuéntanos cómo participas en la operación."
      }
    >
      <div className="space-y-5">
        {!isEmpresa && (
          <SectionCard title="Datos personales" icon="ri-user-line">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Nombre completo" htmlFor="nombreCompleto" required error={errors.nombreCompleto}>
                <input
                  id="nombreCompleto"
                  type="text"
                  autoComplete="name"
                  value={form.nombreCompleto}
                  onChange={(event) => setField("nombreCompleto", event.target.value)}
                  placeholder="Ej. Juan Pérez Díaz"
                  className={inputClass}
                />
              </Field>
              <Field label="Cédula o identificación" htmlFor="cedula" optional error={errors.cedula}>
                <input
                  id="cedula"
                  type="text"
                  value={form.cedula}
                  onChange={(event) => setField("cedula", event.target.value)}
                  placeholder="Ej. 001-1234567-8"
                  className={inputClass}
                />
              </Field>
              <Field
                label="¿Cómo participas?"
                htmlFor="rolVehiculo"
                required
                full
                error={errors.rolVehiculo}
              >
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {rolVehiculoOptions.map((option) => {
                    const selected = form.rolVehiculo === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setField("rolVehiculo", option.value)}
                        aria-pressed={selected}
                        className={`flex cursor-pointer items-center gap-2 rounded-md border px-3.5 py-2.5 text-sm font-label font-medium transition-colors ${
                          selected
                            ? "border-primary-400 bg-primary-50 text-primary-800"
                            : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300"
                        }`}
                      >
                        <i
                          className={`${selected ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"} text-base leading-none`}
                          aria-hidden="true"
                        ></i>
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </div>
          </SectionCard>
        )}

        {isEmpresa && (
          <SectionCard title="Información de la empresa" icon="ri-building-2-line">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="Nombre comercial"
                htmlFor="empresaNombreComercial"
                required
                error={errors.empresaNombreComercial}
              >
                <input
                  id="empresaNombreComercial"
                  type="text"
                  value={form.empresaNombreComercial}
                  onChange={(event) => setField("empresaNombreComercial", event.target.value)}
                  placeholder="Ej. Transportes El Cibao"
                  className={inputClass}
                />
              </Field>
              <Field label="Razón social" htmlFor="empresaRazonSocial" optional>
                <input
                  id="empresaRazonSocial"
                  type="text"
                  value={form.empresaRazonSocial}
                  onChange={(event) => setField("empresaRazonSocial", event.target.value)}
                  placeholder="Ej. Transportes El Cibao SRL"
                  className={inputClass}
                />
              </Field>
              <Field label="RNC" htmlFor="rnc" optional>
                <input
                  id="rnc"
                  type="text"
                  value={form.rnc}
                  onChange={(event) => setField("rnc", event.target.value)}
                  placeholder="Ej. 1-31-12345-6"
                  className={inputClass}
                />
              </Field>
              <Field label="Sitio web" htmlFor="sitioWeb" optional>
                <input
                  id="sitioWeb"
                  type="text"
                  value={form.sitioWeb}
                  onChange={(event) => setField("sitioWeb", event.target.value)}
                  placeholder="https://…"
                  className={inputClass}
                />
              </Field>
              <Field label="Persona de contacto" htmlFor="contactoNombre" required error={errors.contactoNombre}>
                <input
                  id="contactoNombre"
                  type="text"
                  value={form.contactoNombre}
                  onChange={(event) => setField("contactoNombre", event.target.value)}
                  placeholder="Ej. Ana Ramírez"
                  className={inputClass}
                />
              </Field>
              <Field label="Cargo" htmlFor="contactoCargo" optional>
                <input
                  id="contactoCargo"
                  type="text"
                  value={form.contactoCargo}
                  onChange={(event) => setField("contactoCargo", event.target.value)}
                  placeholder="Ej. Gerente de operaciones"
                  className={inputClass}
                />
              </Field>
              <Field label="Redes sociales" htmlFor="redes" optional full hint="Instagram, LinkedIn, etc. (uno por línea o separados por coma)">
                <input
                  id="redes"
                  type="text"
                  value={form.redes}
                  onChange={(event) => setField("redes", event.target.value)}
                  placeholder="@transporteselcibao"
                  className={inputClass}
                />
              </Field>
            </div>
          </SectionCard>
        )}

        <SectionCard title="Contacto y ubicación" icon="ri-map-pin-line">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Teléfono" htmlFor="telefono" required error={errors.telefono}>
              <input
                id="telefono"
                type="tel"
                autoComplete="tel"
                value={form.telefono}
                onChange={(event) => setField("telefono", event.target.value)}
                placeholder="Ej. 809 555 1234"
                className={inputClass}
              />
            </Field>
            <Field label="WhatsApp" htmlFor="whatsapp" optional>
              <input
                id="whatsapp"
                type="tel"
                value={form.whatsapp}
                onChange={(event) => setField("whatsapp", event.target.value)}
                placeholder="Ej. 809 555 1234"
                className={inputClass}
              />
            </Field>
            <Field label={isEmpresa ? "Correo de la empresa" : "Correo electrónico"} htmlFor="email" required error={errors.email}>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(event) => setField("email", event.target.value)}
                placeholder="correo@ejemplo.com"
                className={inputClass}
              />
            </Field>
            <Field label="Provincia" htmlFor="provincia" required error={errors.provincia}>
              <select
                id="provincia"
                value={form.provincia}
                onChange={(event) => setField("provincia", event.target.value)}
                className={inputClass}
              >
                <option value="">Selecciona una provincia</option>
                {provinciasRD.map((provincia) => (
                  <option key={provincia} value={provincia}>
                    {provincia}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Ciudad / municipio" htmlFor="ciudad" required error={errors.ciudad}>
              <input
                id="ciudad"
                type="text"
                value={form.ciudad}
                onChange={(event) => setField("ciudad", event.target.value)}
                placeholder="Ej. Santo Domingo Este"
                className={inputClass}
              />
            </Field>
            <Field
              label={isEmpresa ? "Dirección" : "Dirección o zona de operación"}
              htmlFor="direccionZona"
              optional
            >
              <input
                id="direccionZona"
                type="text"
                value={form.direccionZona}
                onChange={(event) => setField("direccionZona", event.target.value)}
                placeholder="Ej. Av. Duarte #145, Herrera"
                className={inputClass}
              />
            </Field>
          </div>
        </SectionCard>
      </div>
    </StepShell>
  );
}